import React from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { selectDeviceType } from '../../redux/selectors';
import { DH } from '../../utils/DataHook';
import { DROPDOWN_PLACEHOLDER } from './constants';
import { DeviceType } from '../../types/DeviceType';
import { DropdownOption } from './types';
import s from './styles.scss';

interface DropdownViewProps {
  deviceType: DeviceType;
  isOptionsListOpened: boolean;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  invalid?: boolean;
  inputTextValue?: string;
  placeholder?: string;
  options: DropdownOption[];
  handleOptionSelect(option: string): void;
  selectedOption: DropdownOption;
}

export const DropdownView: React.FC<DropdownViewProps> = ({
  deviceType: deviceTypeFromProps,
  isOptionsListOpened,
  invalid,
  inputTextValue,
  required,
  label,
  disabled,
  handleOptionSelect,
  selectedOption,
  placeholder,
  options,
}) => {
  const deviceTypeFromStore = useSelector(selectDeviceType);
  const deviceType = deviceTypeFromProps || deviceTypeFromStore;

  return (
    <div
      data-hook={DH.Dropdown.Container}
      className={cn({
        [s.dropdownContainer]: true,
        [s.desktop]: deviceType === 'desktop',
        [s.mobile]: deviceType === 'mobile',
        [s.invalid]: invalid,
        [s.opened]: isOptionsListOpened,
        [s.filled]: !!inputTextValue,
        [s.required]: required,
      })}
    >
      <div className={s.selectWrapper}>
        <select
          data-hook={DH.Dropdown.Select}
          className={cn({
            [s.input]: true,
            [s.dropdownSelect]: true,
            [s.withPrefixIcon]: selectedOption?.icon,
          })}
          aria-label={label}
          disabled={disabled}
          value={selectedOption?.id || DROPDOWN_PLACEHOLDER}
          onChange={(e) => handleOptionSelect(e.target.value)}
        >
          <option value={DROPDOWN_PLACEHOLDER} disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option
              data-hook={DH.Dropdown.Option(String(option.id))}
              key={option.id}
              value={option.id}
            >
              {option.label}
            </option>
          ))}
        </select>

        {selectedOption?.icon && (
          <img
            data-hook={DH.Dropdown.Icon}
            className={s.inputIcon}
            src={selectedOption.icon}
            alt={selectedOption.value}
          />
        )}
        <div data-hook={DH.Dropdown.Arrow} className={s.dropdownArrow} />
      </div>
    </div>
  );
};

DropdownView.displayName = 'DropdownView';
